import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Section } from '../styles/CommonElements';
import Newsletter from '../components/Newsletter';
import LogoCarousel from '../components/LogoCarousel';
import JoinTeamButton from '../components/JoinTeamButton';
import LearnMoreButton from '../components/LearnMoreButton';
import { useModalContext } from '../App';
import screamImage from '../assets/scream.png';
import SEO from '../components/SEO';

// Modern Hero Section with enhanced styling
const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  position: relative;
  overflow: hidden;
  background: linear-gradient(to right, #f9f9f9, #ffffff);
  margin-top: -1rem;
  width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--primary-color);
  }
`;

// Decorative elements for the hero
const HeroAccent = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 107, 0, 0.1), rgba(255, 143, 66, 0.05));
  top: -200px;
  right: -100px;
  z-index: 0;
`;

const ShapeAccent = styled.div`
  position: absolute;
  width: 150px;
  height: 150px;
  background: rgba(255, 107, 0, 0.08);
  bottom: -30px;
  left: 5%;
  transform: rotate(45deg);
  z-index: 0;
`;

// Canvas for dynamic network visualization
const NetworkCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: 0.5;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const HeroTitleArea = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.75rem;
    left: 0;
    width: 80px;
    height: 4px;
    background: var(--primary-color);
    border-radius: 2px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const HeroTitle = styled.h1`
  font-size: 3.6rem;
  font-weight: 800;
  color: #2a3b4c;
  line-height: 1.2;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
`;

const TextContainer = styled.div`
  max-width: 800px;
  margin-bottom: 2.5rem;
`;

const HeroDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: #546677;
  margin-bottom: 1.2rem;
  font-weight: 400;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    color: #2a3b4c;
    font-weight: 600;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

// Solutions Section
const SolutionsSection = styled(Section)`
  background-color: #f7f9fc;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(to right, var(--primary-color), rgba(255, 107, 0, 0.3));
  }
`;

const ScreamDecoration = styled.div`
  position: absolute;
  bottom: -20px;
  right: 0;
  z-index: 0;
  opacity: 0.07;
  width: 300px;
  height: 300px;
  background-image: url(${screamImage});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom right;
  transform: rotate(5deg);

  @media (max-width: 992px) {
    opacity: 0.05;
    width: 250px;
    height: 250px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SolutionsContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  
  @media (max-width: 992px) {
    padding: 0 1rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const SectionLabel = styled.p`
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--primary-color);
  font-weight: 600;
  margin-bottom: 0.75rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const ContentTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #2a3b4c;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  max-width: 900px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ContentDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #546677;
  margin-bottom: 1.5rem;
  max-width: 900px;
`;

const ExpertiseContainer = styled.div`
  margin: 3rem 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertiseItem = styled.div`
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
  padding: 2rem;
  border-top: 4px solid var(--primary-color);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PartnershipTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;
`;

const KeyPointsList = styled.ul`
  margin: 1.5rem 0 0;
  padding-left: 0;
  list-style: none;
  
  li {
    font-size: 1.05rem;
    margin-bottom: 1rem;
    line-height: 1.6;
    color: #444;
    position: relative;
    padding-left: 1.5rem;
    
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.5rem;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: rgba(255, 107, 0, 0.7);
    }
    
    strong {
      color: #333;
      font-weight: 600;
      display: block;
      margin-bottom: 0.2rem;
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin: 3rem 0 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

// Network Visualization Component
const NetworkVisualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    const setCanvasDimensions = () => {
      const container = canvas.parentElement;
      if (!container) return;
      
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    };
    
    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);
    
    // Create particle system
    const particles: Particle[] = [];
    const connections: Connection[] = [];
    const particleCount = Math.min(window.innerWidth / 25, 60); // Fewer particles for cleaner look
    
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.5 + 0.5; // Smaller particles
        this.speedX = (Math.random() - 0.5) * 0.5; // Slower movement
        this.speedY = (Math.random() - 0.5) * 0.5;
        
        // Colors: oranges, warm grays
        const colors = [
          'rgba(255, 107, 0, 0.4)',   // primary orange
          'rgba(255, 143, 66, 0.3)',  // lighter orange
          'rgba(255, 160, 95, 0.2)',  // pale orange
          'rgba(80, 80, 80, 0.2)',    // dark gray
          'rgba(120, 120, 120, 0.1)', // medium gray
        ];
        
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      
      update() {
        // Update position
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Boundary check
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      
      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    interface Connection {
      from: Particle;
      to: Particle;
      distance: number;
      opacity: number;
    }
    
    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }
    
    // Animation loop
    const animate = () => {
      if (!ctx) return;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      // Create and draw connections
      connections.length = 0;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 120) { // Shorter connection distance
            connections.push({
              from: particles[i],
              to: particles[j],
              distance,
              opacity: 1 - distance / 120
            });
          }
        }
      }
      
      // Draw connections
      connections.forEach(conn => {
        ctx.strokeStyle = `rgba(255, 107, 0, ${conn.opacity * 0.2})`;
        ctx.lineWidth = conn.opacity * 1;
        ctx.beginPath();
        ctx.moveTo(conn.from.x, conn.from.y);
        ctx.lineTo(conn.to.x, conn.to.y);
        ctx.stroke();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
    };
  }, []);
  
  return <NetworkCanvas ref={canvasRef} />;
};

const AboutPage: React.FC = () => {
  // Get the modal opening functions from context
  const { openJoinTeamModal } = useModalContext();
  
  // Handle scrolling to sections when page loads with hash
  useEffect(() => {
    // Check if URL has a hash
    if (window.location.hash) {
      // Get the target element ID from the hash
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      
      if (element) {
        // Wait a bit for the page to fully render
        setTimeout(() => {
          // Get header height for offset
          const headerHeight = document.querySelector('header')?.offsetHeight || 100;
          
          // Calculate the target scroll position
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          
          // Smooth scroll to the target position
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 300);
      }
    }
  }, []);
  
  return (
    <main>
      <SEO 
        title="About Us | Whitby Software Development Experts"
        description="Since 2001, UPTREND Systems has pioneered custom software solutions for businesses throughout Ontario. Our expertise spans custom development, enterprise systems, and cutting-edge business intelligence tools."
        keywords="custom software development Ontario, Whitby software company, enterprise business systems, software development experts, Ontario tech company, business automation solutions"
        canonicalUrl="/about"
        pageType="about"
      />
      <HeroSection>
        <div className="container-fluid p-0 font--jakarta">
          <HeroAccent />
          <ShapeAccent />
          <NetworkVisualization />
          <HeroContent>
            <HeroTitleArea>
              <HeroSubtitle>Since 2001</HeroSubtitle>
              <HeroTitle>Pioneering Custom Software Solutions</HeroTitle>
            </HeroTitleArea>
            
            <TextContainer>
              <HeroDescription>
                At Uptrend Systems, we are visionaries and architects of the digital age. For over <strong>two decades</strong>, 
                we've crafted exceptional software solutions tailored to the unique needs of our clients.
              </HeroDescription>
              
              <HeroDescription>
                What sets us apart is our comprehensive understanding of both business needs and technical possibilities. 
                We bridge the gap between ambitious goals and practical technological solutions, turning complex 
                challenges into streamlined systems that drive growth and efficiency.
              </HeroDescription>
            </TextContainer>
            
            <HeroButtons>
              <LearnMoreButton 
                serviceName="Consultation" 
                buttonText="Request a Consultation"
                isPrimary
              />
              <JoinTeamButton onClick={openJoinTeamModal} />
            </HeroButtons>
          </HeroContent>
        </div>
      </HeroSection>
      
      <SolutionsSection id="why-choose-us">
        <ScreamDecoration />
        <div className="container font--jakarta">
          <SolutionsContent>
            <ContentWrapper>
              <SectionLabel>WHY CHOOSE US</SectionLabel>
              <ContentTitle>Tailored Solutions for Unmatched Performance</ContentTitle>
              <ContentDescription>
                We understand that each business is unique, with its own set of challenges and opportunities. That's why we don't believe in one-size-fits-all answers. Our approach is to dive deep into your specific needs, crafting customized software solutions that not only meet but exceed your expectations.
              </ContentDescription>
              
              <ExpertiseContainer>
                <ExpertiseItem>
                  <PartnershipTitle>Our Expertise</PartnershipTitle>
                  <KeyPointsList>
                    <li><strong>Custom Software Development</strong> Tailored applications built from the ground up to solve your unique business challenges</li>
                    <li><strong>Enterprise Solutions</strong> Integrated systems that connect all aspects of your business operations</li>
                  </KeyPointsList>
                </ExpertiseItem>
                
                <ExpertiseItem>
                  <PartnershipTitle>Industry Solutions</PartnershipTitle>
                  <KeyPointsList>
                    <li><strong>E-Commerce Platforms</strong> Scalable online ordering systems with robust backend management</li>
                    <li><strong>POS & Payment Processing</strong> Secure, user-friendly point-of-sale solutions that streamline transactions</li>
                  </KeyPointsList>
                </ExpertiseItem>
                
                <ExpertiseItem>
                  <PartnershipTitle>Data & Analytics</PartnershipTitle>
                  <KeyPointsList>
                    <li><strong>Business Intelligence</strong> Data analytics and reporting tools that transform raw data into actionable insights</li>
                    <li><strong>Performance Optimization</strong> System monitoring and enhancement to ensure peak operational efficiency</li>
                  </KeyPointsList>
                </ExpertiseItem>
              </ExpertiseContainer>
              
              <ButtonsContainer>
                <JoinTeamButton onClick={openJoinTeamModal} />
                <LearnMoreButton 
                  serviceName="Consultation" 
                  className="full-width-button"
                  buttonText="Request a Consultation"
                  isPrimary
                />
              </ButtonsContainer>
            </ContentWrapper>
          </SolutionsContent>
        </div>
      </SolutionsSection>
      
      <LogoCarousel columnCount={5} title="Trusted by Canada's Leading Brands" subtitle="We're proud to work with forward-thinking organizations across various industries" />
      
      <Newsletter />
    </main>
  );
};

export default AboutPage;